import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Menu, X, LogIn } from 'lucide-react';
import { cn } from '../../lib/utils';
import logoImage from '../../assets/images/logo.png';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Close mobile menu after navigation
  const handleMobileNav = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (totalHeight > 0) {
        const progress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    } else if (location.pathname !== '/') {
      window.location.href = '/#courses-section';
    }
  };


  return (
    <>
      <nav 
        dir="rtl" 
        className={cn(
          "fixed z-40 text-gray-800 transition-all duration-500 ease-in-out",
          isScrolled 
            ? "top-3 left-3 right-3 h-16 bg-white shadow-lg rounded-[2rem]" 
            : "inset-x-0 top-0 h-24 bg-transparent"
        )}
        style={{
          backdropFilter: isScrolled ? 'none' : 'blur(15px)',
          WebkitBackdropFilter: isScrolled ? 'none' : 'blur(15px)',
          background: isScrolled ? 'white' : 'rgba(255, 255, 255, 0.4)',
        }}
      >
        <div className="relative h-full">
          <div className="max-w mx-auto px-3 sm:px-4 lg:px-6 h-full relative overflow-hidden">
            {/* Scroll Progress Bar - Inside navbar at bottom edge */}
            <div className={cn(
              "absolute inset-x-3 sm:inset-x-4 lg:inset-x-6 bottom-0 h-0.5 transition-all duration-500 rounded-full",
              isScrolled ? "opacity-100" : "opacity-0"
            )}>
              <div
                className="h-full bg-gradient-to-r from-primary via-primary to-primary-dark transition-all duration-300 ease-out rounded-full"
                style={{ width: `${scrollProgress}%` }}
              />
            </div>
            <div className="relative flex items-center justify-between h-full">
              {/* Logo - Centered */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-auto">
                <div className="flex-shrink-0">
                  <Link to="/" className="flex items-center justify-center group">
                    <img
                      src={logoImage}
                      alt="منصة حسام رسلان"
                      className={cn(
                        "w-auto object-contain transition-all duration-500 group-hover:scale-105",
                        isScrolled ? "h-9 sm:h-10" : "h-14 sm:h-16 md:h-20"
                      )}
                    />
                    {/* Logo icon - removed */}
                    <div className="hidden relative w-8 h-8 sm:w-9 sm:h-9 mr-2 rounded-lg bg-gradient-to-tr from-primary to-primary-dark group-hover:shadow-lg transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-white opacity-20 group-hover:opacity-30 transition-opacity"></div>
                      <svg className="w-5 h-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      </svg>
                      <div className="absolute inset-0 border border-white/10 rounded-lg"></div>
                    </div>
                    
                    {/* Brand name - hidden */}
                    <div className="hidden relative hidden xs:block">
                      <span className="text-xl sm:text-2xl font-extrabold bg-gradient-to-r from-primary via-primary-dark to-primary bg-clip-text text-transparent group-hover:opacity-90 transition-opacity font-['Reem_Kufi'] tracking-[0.5px]">
                        الـمُـبِـيْـنْ
                      </span>
                      <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
                      <div className="absolute -top-1 right-0 w-2 h-2 rounded-full bg-primary/20 group-hover:bg-primary/40 transition-all duration-300"></div>
                    </div>
                  </Link>
                </div>
              </div>
              
              {/* Left side - Navigation Links (RTL: appears on right) */}
              <div className="flex items-center flex-1 min-w-0 justify-start">
                {/* Desktop Navigation - Hidden on mobile */}
                <div className="hidden lg:flex lg:items-center lg:gap-2 lg:space-x-reverse">
                <Link 
                  to="/" 
                  className={cn(
                    "relative px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300",
                    location.pathname === '/' 
                      ? "text-primary bg-primary/10" 
                      : "text-gray-700 hover:text-primary hover:bg-primary/5"
                  )}
                >
                  الرئيسية
                  {location.pathname === '/' && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
                  )}
                </Link>
                <button
                  onClick={scrollToCourses}
                  className="px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 text-gray-700 hover:text-primary hover:bg-primary/5 cursor-pointer"
                >
                  الدورات
                </button>
                {isAuthenticated && (
                  <Link
                    to="/enrolled-courses"
                    className={cn(
                      "relative px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300",
                      location.pathname === '/enrolled-courses' 
                        ? "text-primary bg-primary/10" 
                        : "text-gray-700 hover:text-primary hover:bg-primary/5"
                    )}
                  >
                    دوراتي
                    {location.pathname === '/enrolled-courses' && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
                    )}
                  </Link>
                )}
                {isAuthenticated && (
                  <Link
                    to="/my-lectures"
                    className={cn(
                      "relative px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300",
                      location.pathname === '/my-lectures' 
                        ? "text-primary bg-primary/10" 
                        : "text-gray-700 hover:text-primary hover:bg-primary/5"
                    )}
                  >
                    محاضراتى
                    {location.pathname === '/my-lectures' && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
                    )}
                  </Link>
                )}
                </div>
              </div>
              
              {/* Center - Logo (duplicate removed as already exists above) */}
                    
              {/* Right side - Actions and Auth (RTL: appears on left) */}
              <div className="flex items-center gap-2 sm:gap-3 justify-end flex-1 min-w-0">
                {/* Parent Monitoring - Hidden on mobile */}
                <div className="hidden lg:flex items-center">
                  <Link 
                    to="/parent-monitoring" 
                    className={cn(
                      "px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center gap-2",
                      location.pathname === '/parent-monitoring'
                        ? "text-primary bg-primary/10"
                        : "text-gray-700 hover:text-primary hover:bg-primary/5"
                    )}
                  >
                    <Users className="w-4 h-4" />
                    <span>متابعة ولي الأمر</span>
                  </Link>
                </div>

                {/* Auth Section - Hidden on mobile */}
                <div className="hidden lg:flex lg:items-center lg:gap-3">
                {isAuthenticated ? (
                  <>
                    <div className="relative mr-3 flex items-center space-x-3">
                      <div className="flex items-center gap-3 px-4 py-2.5 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-sm group hover:shadow-md transition-all duration-300">
                        {/* User avatar/icon */}
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        
                        {/* Welcome text */}
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 leading-none">مرحباً</span>
                          <span className="text-sm font-bold text-gray-800 mt-0.5">
                            {currentUser?.name}
                          </span>
                        </div>
                      </div>
                      
                      {/* Logout button */}
                      <button
                        onClick={logout}
                        className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 group flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        تسجيل الخروج
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-white text-sm font-semibold rounded-2xl shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-300"
                    >
                      <Users size={18} className="group-hover:scale-110 transition-transform" />
                      <span>إنشاء حساب</span>
                    </Link>
                    <Link
                      to="/login"
                      className="group flex items-center gap-2 px-5 py-2.5 bg-white/80 backdrop-blur-sm border-2 border-primary text-primary text-sm font-semibold rounded-2xl shadow-sm hover:shadow-md hover:bg-primary/5 hover:scale-105 active:scale-95 transition-all duration-300"
                    >
                      <LogIn size={18} className="group-hover:translate-x-1 transition-transform" />
                      <span>سجل دخول</span>
                    </Link>
                  </>
                )}
                </div>
                
                {/* Mobile menu button */}
                <div className="flex items-center lg:hidden">
                  <button 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                    className="inline-flex items-center justify-center rounded-xl p-2.5 text-gray-700 hover:text-primary hover:bg-primary/10 active:bg-primary/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 touch-manipulation"
                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isMobileMenuOpen}
                  >
                    {isMobileMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {isMobileMenuOpen && (
            <div className={cn(
              "lg:hidden fixed inset-x-0 bg-white border-t border-gray-200 shadow-2xl z-50 transition-all duration-500",
              isScrolled ? "top-[4.5rem]" : "top-24"
            )}>
              <div className="space-y-1 px-4 pt-4 pb-6 max-h-[calc(100vh-5rem)] overflow-y-auto bg-white">
                <Link 
                  to="/" 
                  onClick={handleMobileNav}
                  className={cn(
                    "block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                    location.pathname === '/' 
                      ? "text-primary bg-primary/10 border-r-4 border-primary" 
                      : "text-gray-700 active:bg-primary/10"
                  )}
                >
                  الرئيسية
                </Link>
                <button
                  onClick={() => { handleMobileNav(); scrollToCourses(); }}
                  className="block w-full text-right rounded-xl px-4 py-3.5 text-base font-semibold text-gray-700 active:bg-primary/10 active:scale-[0.98] transition-all duration-200 touch-manipulation"
                >
                  الدورات
                </button>
                {isAuthenticated && (
                  <Link
                    to="/enrolled-courses"
                    onClick={handleMobileNav}
                    className={cn(
                      "block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                      location.pathname === '/enrolled-courses' 
                        ? "text-primary bg-primary/10 border-r-4 border-primary" 
                        : "text-gray-700 active:bg-primary/10"
                    )}
                  >
                    دوراتي
                  </Link>
                )}
                {isAuthenticated && (
                  <Link
                    to="/my-lectures"
                    onClick={handleMobileNav}
                    className={cn(
                      "block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                      location.pathname === '/my-lectures' 
                        ? "text-primary bg-primary/10 border-r-4 border-primary" 
                        : "text-gray-700 active:bg-primary/10"
                    )}
                  >
                    محاضراتى
                  </Link>
                )}
                <Link
                  to="/parent-monitoring"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                    location.pathname === '/parent-monitoring'
                      ? "text-primary bg-primary/10 border-r-4 border-primary"
                      : "text-gray-700 active:bg-primary/10"
                  )}
                >
                  <Users className="w-5 h-5 flex-shrink-0" />
                  <span>متابعة ولي الأمر</span>
                </Link>
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-4"></div>
                
                {/* User section for mobile */}
                {isAuthenticated ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-md flex-shrink-0">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-0.5">مرحباً</p>
                        <p className="text-sm font-bold text-gray-800 truncate">{currentUser?.name}</p>
                      </div>
                    </div>
                    <button
                      onClick={logout}
                      className="block w-full text-center rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3.5 text-base font-semibold shadow-md active:scale-[0.98] active:shadow-sm transition-all duration-200 touch-manipulation"
                    >
                      تسجيل الخروج
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Link 
                      to="/register" 
                      onClick={handleMobileNav}
                      className="block w-full text-center rounded-xl bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-3.5 text-base font-semibold shadow-md active:scale-[0.98] active:shadow-sm transition-all duration-200 touch-manipulation"
                    >
                      إنشاء حساب جديد
                    </Link>
                    <Link 
                      to="/login" 
                      onClick={handleMobileNav}
                      className="block w-full text-center rounded-xl bg-white border-2 border-primary text-primary px-4 py-3.5 text-base font-semibold active:bg-primary/5 active:scale-[0.98] transition-all duration-200 touch-manipulation"
                    >
                      سجل الدخول
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
      <div className="h-20" />
    </>
  );
};


export default Navbar; 